/**
 * Zod validation schemas for HR routes. Every request body/param/query that
 * crosses the API boundary parses through one of these. Optional fields use
 * `.optional()` (TypeScript `exactOptionalPropertyTypes` is on, so when these
 * propagate into TS shapes via `z.infer<...>` the optionality is preserved).
 */
import { z } from 'zod';
// ---------------------------------------------------------------------------
// Common parametrics
// ---------------------------------------------------------------------------
const UuidParam = z.object({ id: z.string().uuid() });
export const HrIdParamSchema = UuidParam;
export const HrUserIdParamSchema = z.object({ userId: z.string().uuid() });
// ---------------------------------------------------------------------------
// Access (Phase 1)
// ---------------------------------------------------------------------------
export const GrantHrAccessRequestSchema = z.object({
    userId: z.string().uuid(),
});
// ---------------------------------------------------------------------------
// Settings (Phase 1)
// ---------------------------------------------------------------------------
const MeetingProviderSchema = z.enum(['teams', 'meet', 'zoom']);
// Interviews are human-only.
const InterviewerModeSchema = z.enum(['human']);
export const PatchHrSettingsRequestSchema = z
    .object({
    driveEmployeeFolderUrl: z.string().trim().min(1).optional(),
    driveCandidateCvFolderUrl: z.string().trim().min(1).optional(),
    driveOfferLetterFolderUrl: z.string().trim().min(1).optional(),
    drivePolicyFolderUrl: z.string().trim().min(1).optional(),
    driveContractsFolderUrl: z.string().trim().min(1).optional(),
    // Interview scheduling (ATS Step 7)
    meetingProvider: MeetingProviderSchema.nullable().optional(),
    // Application intake + auto-screen (ATS Step 4-5)
    applicationInboxAddress: z.string().trim().email().nullable().optional(),
    applicationIntakeEnabled: z.boolean().optional(),
    autoScreenScoreThreshold: z.number().int().min(0).max(100).optional(),
})
    .strict();
// Step 7 — schedule an interview for a candidate.
export const ScheduleHrInterviewRequestSchema = z
    .object({
    interviewerMode: InterviewerModeSchema.default('human'),
    interviewerUserId: z.string().uuid().optional(),
    scheduledAt: z.string().datetime(),
    durationMins: z.number().int().min(5).max(480).optional(),
    meetingProvider: MeetingProviderSchema.nullable().optional(),
    meetingUrl: z.string().trim().url().max(2000).nullable().optional(),
    noteTakerBot: z.boolean().optional(),
    generateQuestions: z.boolean().optional(),
    sendInvite: z.boolean().optional(),
})
    .strict();
// Edit an interview — paste/replace the meeting link, toggle note-taker, and/or
// (late) schedule a placeholder interview by supplying scheduledAt.
export const UpdateHrInterviewRequestSchema = z
    .object({
    meetingUrl: z.string().trim().url().max(2000).nullable().optional(),
    noteTakerBot: z.boolean().optional(),
    scheduledAt: z.string().datetime().optional(),
    durationMins: z.number().int().min(5).max(480).optional(),
    meetingProvider: MeetingProviderSchema.nullable().optional(),
    generateQuestions: z.boolean().optional(),
    questions: z.array(z.string().trim().max(1000)).max(30).optional(),
    sendInvite: z.boolean().optional(),
})
    .strict()
    .refine((v) => Object.keys(v).length > 0, {
    message: 'Provide at least one field to update.',
});
// Step 9 — complete an interview: attach a transcript to analyze.
export const CompleteHrInterviewRequestSchema = z
    .object({
    transcript: z.string().trim().min(1).max(200_000),
})
    .strict();
// MeetingBaas API key — note-taker meeting bot (D4).
export const SetMeetingBaasKeyRequestSchema = z
    .object({
    apiKey: z.string().trim().min(1).max(400),
})
    .strict();
// Step 12 — generate / regenerate an offer draft (optional steering).
export const CreateHrOfferRequestSchema = z
    .object({
    instructions: z.string().trim().max(4000).optional(),
})
    .strict();
// Edit the offer letter body before approval.
export const UpdateHrOfferRequestSchema = z
    .object({
    bodyHtml: z.string().min(1).max(100_000),
})
    .strict();
// ---------------------------------------------------------------------------
// Employees (Phase 1)
// ---------------------------------------------------------------------------
const EmploymentTypeSchema = z.enum(['full_time', 'part_time', 'contractor', 'intern']);
const EmployeeStatusSchema = z.enum(['active', 'on_leave', 'terminated']);
const CustomFieldsSchema = z.record(z.string().max(80), z.string().max(2000));
export const CreateHrEmployeeRequestSchema = z
    .object({
    fullName: z.string().trim().min(1).max(200),
    workEmail: z.string().trim().email().optional(),
    personalEmail: z.string().trim().email().optional(),
    phone: z.string().trim().max(40).optional(),
    jobTitle: z.string().trim().max(200).optional(),
    department: z.string().trim().max(120).optional(),
    employmentType: EmploymentTypeSchema.optional(),
    status: EmployeeStatusSchema.optional(),
    managerId: z.string().uuid().nullable().optional(),
    hireDate: z.string().date().optional(),
    probationEndsOn: z.string().date().optional(),
    birthday: z.string().date().optional(),
    location: z.string().trim().max(200).optional(),
    notes: z.string().max(20_000).optional(),
    avatarUrl: z.string().url().optional(),
    customFields: CustomFieldsSchema.optional(),
})
    .strict();
export const UpdateHrEmployeeRequestSchema = z
    .object({
    fullName: z.string().trim().min(1).max(200).optional(),
    workEmail: z.string().trim().email().nullable().optional(),
    personalEmail: z.string().trim().email().nullable().optional(),
    phone: z.string().trim().max(40).nullable().optional(),
    jobTitle: z.string().trim().max(200).nullable().optional(),
    department: z.string().trim().max(120).nullable().optional(),
    employmentType: EmploymentTypeSchema.optional(),
    status: EmployeeStatusSchema.optional(),
    managerId: z.string().uuid().nullable().optional(),
    hireDate: z.string().date().nullable().optional(),
    probationEndsOn: z.string().date().nullable().optional(),
    birthday: z.string().date().nullable().optional(),
    location: z.string().trim().max(200).nullable().optional(),
    notes: z.string().max(20_000).nullable().optional(),
    avatarUrl: z.string().url().nullable().optional(),
    customFields: CustomFieldsSchema.nullable().optional(),
})
    .strict();
export const HrEmployeeQuerySchema = z
    .object({
    search: z.string().trim().max(200).optional(),
    status: EmployeeStatusSchema.optional(),
    department: z.string().trim().max(120).optional(),
    limit: z.coerce.number().int().min(1).max(500).optional(),
})
    .strict();
// ---------------------------------------------------------------------------
// Jobs (Phase 2)
// ---------------------------------------------------------------------------
const JobStatusSchema = z.enum(['open', 'paused', 'closed']);
const AttendanceTypeSchema = z.enum(['remote', 'on_site', 'hybrid']);
const NeededSkillsSchema = z.array(z.string().trim().min(1).max(120)).max(50);
export const CreateHrJobRequestSchema = z
    .object({
    title: z.string().trim().min(1).max(200),
    department: z.string().trim().max(120).optional(),
    location: z.string().trim().max(200).optional(),
    employmentType: EmploymentTypeSchema.optional(),
    seniority: z.string().trim().max(40).optional(),
    description: z.string().max(40_000).optional(),
    responsibilities: z.string().max(40_000).optional(),
    requirements: z.string().max(40_000).optional(),
    niceToHave: z.string().max(40_000).optional(),
    salaryRange: z.string().trim().max(120).optional(),
    status: JobStatusSchema.optional(),
    // ATS redesign (Step 1 + Step 2).
    neededSkills: NeededSkillsSchema.optional(),
    expectedSalary: z.string().trim().max(120).optional(),
    attendanceType: AttendanceTypeSchema.optional(),
    hrNote: z.string().max(20_000).optional(),
    companyDetails: z.string().max(40_000).optional(),
    generatedDescription: z.string().max(40_000).optional(),
    applyByDate: z.string().date().optional(),
})
    .strict();
export const UpdateHrJobRequestSchema = z
    .object({
    title: z.string().trim().min(1).max(200).optional(),
    department: z.string().trim().max(120).nullable().optional(),
    location: z.string().trim().max(200).nullable().optional(),
    employmentType: EmploymentTypeSchema.optional(),
    seniority: z.string().trim().max(40).nullable().optional(),
    description: z.string().max(40_000).nullable().optional(),
    responsibilities: z.string().max(40_000).nullable().optional(),
    requirements: z.string().max(40_000).nullable().optional(),
    niceToHave: z.string().max(40_000).nullable().optional(),
    salaryRange: z.string().trim().max(120).nullable().optional(),
    status: JobStatusSchema.optional(),
    // ATS redesign (Step 1 + Step 2).
    neededSkills: NeededSkillsSchema.optional(),
    expectedSalary: z.string().trim().max(120).nullable().optional(),
    attendanceType: AttendanceTypeSchema.nullable().optional(),
    hrNote: z.string().max(20_000).nullable().optional(),
    companyDetails: z.string().max(40_000).nullable().optional(),
    generatedDescription: z.string().max(40_000).nullable().optional(),
    applyByDate: z.string().date().nullable().optional(),
})
    .strict();
export const GenerateHrJobDescriptionRequestSchema = z
    .object({
    title: z.string().trim().min(1).max(200),
    department: z.string().trim().max(120).optional(),
    location: z.string().trim().max(200).optional(),
    employmentType: EmploymentTypeSchema.optional(),
    seniority: z.string().trim().max(40).optional(),
    attendanceType: AttendanceTypeSchema.optional(),
    neededSkills: NeededSkillsSchema.optional(),
    expectedSalary: z.string().trim().max(120).optional(),
    hrNote: z.string().max(20_000).optional(),
})
    .strict();
// ---------------------------------------------------------------------------
// Candidates (Phase 2)
// ---------------------------------------------------------------------------
const CandidateStageSchema = z.enum([
    'applied',
    'screen',
    'interview',
    'after_interview',
    'offer',
    'hired',
    'rejected',
]);
export const CreateHrCandidateRequestSchema = z
    .object({
    jobId: z.string().uuid(),
    fullName: z.string().trim().min(1).max(200),
    email: z.string().trim().email().optional(),
    phone: z.string().trim().max(40).optional(),
    // LinkedIn: accept a full URL OR a bare handle (e.g. "jane-doe") — recruiters
    // routinely paste just the handle, so don't force a valid URL.
    linkedinUrl: z.string().trim().max(400).optional(),
    location: z.string().trim().max(200).optional(),
    source: z.string().trim().max(120).optional(),
    notes: z.string().max(20_000).optional(),
})
    .strict();
export const UpdateHrCandidateRequestSchema = z
    .object({
    fullName: z.string().trim().min(1).max(200).optional(),
    email: z.string().trim().email().nullable().optional(),
    phone: z.string().trim().max(40).nullable().optional(),
    // Accept a full URL OR a bare handle (see CreateHrCandidateRequestSchema).
    linkedinUrl: z.string().trim().max(400).nullable().optional(),
    location: z.string().trim().max(200).nullable().optional(),
    source: z.string().trim().max(120).nullable().optional(),
    stage: CandidateStageSchema.optional(),
    notes: z.string().max(20_000).nullable().optional(),
})
    .strict();
// Step 6 — reject a candidate (Screen gate) with an optional custom message
// and an opt-out of the notification email.
export const RejectHrCandidateRequestSchema = z
    .object({
    reason: z.string().trim().max(4000).optional(),
    sendEmail: z.boolean().optional(),
})
    .strict();
export const HrCandidateQuerySchema = z
    .object({
    jobId: z.string().uuid().optional(),
    stage: CandidateStageSchema.optional(),
    search: z.string().trim().max(200).optional(),
    limit: z.coerce.number().int().min(1).max(500).optional(),
})
    .strict();
// ---------------------------------------------------------------------------
// HR Mail (Phase 3)
// ---------------------------------------------------------------------------
const EmailAddress = z.string().trim().email();
// HR mailbox IMAP/SMTP credentials (P11). App-password auth; works with Gmail,
// Microsoft Exchange/Office 365, or any IMAP/SMTP host.
export const SetHrMailAccountRequestSchema = z
    .object({
    email: EmailAddress,
    imapHost: z.string().trim().min(1).max(255),
    imapPort: z.coerce.number().int().min(1).max(65535),
    smtpHost: z.string().trim().min(1).max(255),
    smtpPort: z.coerce.number().int().min(1).max(65535),
    appPassword: z.string().trim().min(1).max(500),
    useTls: z.boolean().optional(),
})
    .strict();
export const HrMailMessageIdParamSchema = z.object({ id: z.string().uuid() });
export const HrMailQuerySchema = z
    .object({
    folder: z.enum(['inbox', 'sent', 'drafts', 'archive']).optional(),
    search: z.string().trim().max(200).optional(),
    limit: z.coerce.number().int().min(1).max(200).optional(),
})
    .strict();
export const HrMailSendRequestSchema = z
    .object({
    to: z.array(EmailAddress).min(1).max(100),
    cc: z.array(EmailAddress).max(100).optional(),
    bcc: z.array(EmailAddress).max(100).optional(),
    subject: z.string().trim().min(1).max(400),
    bodyHtml: z.string().max(500_000),
    templateId: z.string().uuid().optional(),
    variables: z.record(z.string().max(200), z.string().max(8000)).optional(),
    attachmentIds: z.array(z.string().uuid()).max(20).optional(),
})
    .strict();
export const CreateHrMailTemplateRequestSchema = z
    .object({
    name: z.string().trim().min(1).max(200),
    subject: z.string().trim().min(1).max(400),
    bodyHtml: z.string().max(500_000),
})
    .strict();
export const UpdateHrMailTemplateRequestSchema = z
    .object({
    name: z.string().trim().min(1).max(200).optional(),
    subject: z.string().trim().min(1).max(400).optional(),
    bodyHtml: z.string().max(500_000).optional(),
})
    .strict();
// ---------------------------------------------------------------------------
// HR Agents (Phase 5)
// ---------------------------------------------------------------------------
const HrAgentKindSchema = z.enum([
    'cv_scorer',
    'jd_writer',
    'interview_questions',
    'outreach_writer',
    'review_drafter',
    'exit_summarizer',
    'policy_qa',
]);
export const HrAgentKindParamSchema = z.object({ kind: HrAgentKindSchema });
export const UpdateHrAgentRequestSchema = z
    .object({
    model: z.string().trim().min(1).max(120).optional(),
    temperature: z.number().min(0).max(2).optional(),
    maxTokens: z.number().int().min(64).max(32_000).optional(),
    systemPrompt: z.string().max(50_000).optional(),
    enabled: z.boolean().optional(),
})
    .strict();
export const HrAgentRunRequestSchema = z
    .object({
    inputs: z.record(z.string(), z.unknown()),
})
    .strict();
// ---------------------------------------------------------------------------
// Documents (Phase 6)
// ---------------------------------------------------------------------------
export const HrDocumentEntityParamSchema = z.object({
    entityKind: z.enum(['employee', 'candidate', 'job']),
    entityId: z.string().uuid(),
});
// ---------------------------------------------------------------------------
// Policies (Phase 7)
// ---------------------------------------------------------------------------
export const HrPolicyQaRequestSchema = z
    .object({
    question: z.string().trim().min(1).max(2000),
})
    .strict();
// ---------------------------------------------------------------------------
// Onboarding (Phase 8)
// ---------------------------------------------------------------------------
const OnboardingTaskDef = z.object({
    title: z.string().trim().min(1).max(200),
    description: z.string().max(2000).nullable(),
    assigneeKind: z.enum(['manager', 'new_hire', 'it', 'hr', 'buddy']),
    dueOffsetDays: z.number().int().min(-365).max(365),
});
export const CreateHrOnboardingTemplateRequestSchema = z
    .object({
    name: z.string().trim().min(1).max(200),
    description: z.string().max(2000).optional(),
    tasks: z.array(OnboardingTaskDef).min(1).max(50),
})
    .strict();
export const UpdateHrOnboardingTemplateRequestSchema = z
    .object({
    name: z.string().trim().min(1).max(200).optional(),
    description: z.string().max(2000).nullable().optional(),
    tasks: z.array(OnboardingTaskDef).min(1).max(50).optional(),
})
    .strict();
export const StartHrOnboardingRequestSchema = z
    .object({
    employeeId: z.string().uuid(),
    templateId: z.string().uuid(),
})
    .strict();
export const CompleteHrOnboardingTaskRequestSchema = z
    .object({
    completed: z.boolean(),
})
    .strict();
//# sourceMappingURL=hr.schema.js.map